
import React, { useState, useEffect } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export function EnhancedSearch({ onSearch, onFilterChange, filters }) {
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [showFilters, setShowFilters] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const popularSearches = [
    { label: 'Monitor', search: null, category: 'best-computer-monitors' },
    { label: '4k Monitor', search: '4k', category: 'best-computer-monitors' },
    { label: 'Curved Monitor', search: 'curved', category: 'best-computer-monitors' },
    { label: 'Gaming Mouse', search: 'gaming', category: 'best-gaming-mice' },
    { label: 'Gaming Headset', search: 'gaming', category: 'best-gaming-headsets' },
    { label: 'Keyboard', search: null, category: 'best-mechanical-keyboards' },
    { label: 'Graphics Card', search: null, category: 'best-graphics-cards-gpu' }
  ];

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      onSearch(searchTerm);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, onSearch]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch(searchTerm);
  };

  const handleSuggestionClick = (suggestion) => {
    if (typeof suggestion === 'object') {
      // Set category and search via filter change
      setSearchTerm(suggestion.search || '');
      if (onFilterChange) {
        onFilterChange({ 
          ...filters, 
          category: suggestion.category,
          search: suggestion.search || ''
        });
      }
    } else {
      // Legacy string support
      setSearchTerm(suggestion);
      onSearch(suggestion);
    }
  };

  return (
    <div className="relative max-w-2xl mx-auto">
      {/* Search Bar */}
      <form onSubmit={handleSearchSubmit} className="relative">
        <div className="relative group">
          <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 w-5 h-5 group-focus-within:text-blue-500 transition-colors z-50" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search for products, brands, or categories..."
            className="w-full pl-12 pr-20 py-2 md:py-4 text-lg bg-white/80 dark:bg-dark-card/80 backdrop-blur-sm border border-slate-200 dark:border-dark-primary rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all shadow-lg dark:shadow-2xl placeholder-slate-400 dark:placeholder-slate-500 text-slate-800 dark:text-white"
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  onSearch('');
                }}
                className="p-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="p-4 text-slate-400 hover:text-blue-500 transition-colors"
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>

      {/* Popular Searches */}
      {!searchTerm && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex gap-2 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-blue-500 scrollbar-track-transparent"
        >
          <span className="flex-shrink-0 px-3 py-1 text-xs md:text-sm text-slate-600 dark:text-dark-600">Popular:</span>
          {popularSearches.map((search) => (
            <button
              key={search.label}
              onClick={() => handleSuggestionClick(search)}
              className="flex-shrink-0 px-3 py-1 text-xs md:text-sm bg-slate-100 dark:bg-dark-tertiary text-slate-600 dark:text-slate-300 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 transition-colors whitespace-nowrap"
            >
              {search.label}
            </button>
          ))}
        </motion.div>
      )}

      {/* Quick Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 p-4 bg-white/95 dark:bg-dark-card/95 backdrop-blur-sm border border-slate-200 dark:border-dark-primary rounded-xl shadow-xl dark:shadow-2xl z-50"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Price Range
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minPrice}
                    onChange={(e) => onFilterChange({ ...filters, minPrice: e.target.value })}
                    className="w-1/2 flex-1 px-3 py-2 bg-white dark:bg-dark-tertiary border border-slate-200 dark:border-dark-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-slate-800 dark:text-white"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxPrice}
                    onChange={(e) => onFilterChange({ ...filters, maxPrice: e.target.value })}
                    className="w-1/2 flex-1 px-3 py-2 bg-white dark:bg-dark-tertiary border border-slate-200 dark:border-dark-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-slate-800 dark:text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Sort By
                </label>
                <select
                  value={filters.sort}
                  onChange={(e) => onFilterChange({ ...filters, sort: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-dark-tertiary border border-slate-200 dark:border-dark-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-slate-800 dark:text-white"
                >
                  <option value="featured">Featured</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="rating_desc">Highest Rated</option>
                  <option value="discount_desc">Biggest Discounts</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Availability
                </label>
                <select
                  value={filters.stockStatus}
                  onChange={(e) => onFilterChange({ ...filters, stockStatus: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-dark-tertiary border border-slate-200 dark:border-dark-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-slate-800 dark:text-white"
                >
                  <option value="">All Items</option>
                  <option value="in_stock">In Stock Only</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
